import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { getScheduleSlots, getScheduleSlotTypes, createScheduleSlot, updateScheduleSlot, deleteScheduleSlot } from '../../services/calendar.service';
import { ScheduleSlot, ScheduleSlotType, CreateScheduleSlotDto, UpdateScheduleSlotDto } from '../../types/calendar';
import { ScheduleSlotEditModal } from './ScheduleSlotEditModal';

const daysOfWeek = [
  { id: 1, name: 'Пн' },
  { id: 2, name: 'Вт' },
  { id: 3, name: 'Ср' },
  { id: 4, name: 'Чт' },
  { id: 5, name: 'Пт' },
  { id: 6, name: 'Сб' },
  { id: 7, name: 'Нд' },
];

const DAY_START_TIME = '03:00:00';

export const SlotsTab: React.FC = () => {
  const [slots, setSlots] = useState<ScheduleSlot[]>([]);
  const [slotTypes, setSlotTypes] = useState<ScheduleSlotType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState<Partial<ScheduleSlot> | Partial<CreateScheduleSlotDto> | null>(null);
  const [slotToDelete, setSlotToDelete] = useState<ScheduleSlot | null>(null);

  const fetchData = async () => {
    try {
      setError(null);
      const [slotsData, slotTypesData] = await Promise.all([
        getScheduleSlots(),
        getScheduleSlotTypes(),
      ]);
      setSlots(slotsData);
      setSlotTypes(slotTypesData);
    } catch (err) {
      console.error('Failed to fetch schedule slots data', err);
      setError('Не вдалося завантажити дані для слотів розкладу.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenCreateModal = (dayOfWeek: number, timeStart?: string) => {
    setEditingSlot({ dayOfWeek, timeStart, slotTypeId: slotTypes[0]?.id });
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (slot: ScheduleSlot) => {
    setEditingSlot(JSON.parse(JSON.stringify(slot)));
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSlot(null);
  };

  const handleSave = async (data: CreateScheduleSlotDto | (UpdateScheduleSlotDto & { id: number })) => {
    try {
      setError(null);
      const payload: any = { ...data };

      if (payload.durationMinutes) payload.durationMinutes = Number(payload.durationMinutes);
      if (payload.notificationMinutesBefore) payload.notificationMinutesBefore = Number(payload.notificationMinutesBefore);

      if ('id' in payload) {
        const { id, dayName, slotType, dayOfWeek, ...updateData } = payload;
        await updateScheduleSlot(id, updateData as UpdateScheduleSlotDto);
      } else {
        await createScheduleSlot(payload as CreateScheduleSlotDto);
      }
      
      await fetchData();
      handleCloseModal();
    } catch (err) {
      console.error('Failed to save schedule slot', err);
      setError('Не вдалося зберегти слот. Перевірте правильність введених даних.');
    }
  };

  const handleDeleteRequest = (id: number) => {
    const slot = slots.find(s => s.id === id);
    if (slot) {
        setSlotToDelete(slot);
    }
    handleCloseModal();
  };

  const handleConfirmDelete = async () => {
    if (!slotToDelete) return;
    try {
        setError(null);
        await deleteScheduleSlot(slotToDelete.id);
        await fetchData();
        setSlotToDelete(null);
    } catch (err) {
        console.error('Failed to delete schedule slot', err);
        setError('Не вдалося видалити слот.');
        setSlotToDelete(null);
    }
  };

  const renderConfirmDeleteDialog = () => {
      if (!slotToDelete) return null;
      return (
        <Dialog open={true} onClose={() => setSlotToDelete(null)}>
            <DialogTitle>Підтвердити видалення</DialogTitle>
            <DialogContent>
                <DialogContentText component="div">
                    Ви впевнені, що хочете видалити слот "<b>{slotToDelete.slotName || slotToDelete.slotType.name}</b>" о <b>{slotToDelete.timeStart.substring(0, 5)}</b>?
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setSlotToDelete(null)}>Скасувати</Button>
                <Button onClick={handleConfirmDelete} color="error" variant="contained">Видалити</Button>
            </DialogActions>
        </Dialog>
      );
  }

  const renderTimelineContent = () => {
    const slotsByTimeAndDay = new Map<string, Map<number, ScheduleSlot>>();
    slots.forEach(slot => {
        const time = slot.timeStart;
        if (!slotsByTimeAndDay.has(time)) {
            slotsByTimeAndDay.set(time, new Map<number, ScheduleSlot>());
        }
        slotsByTimeAndDay.get(time)!.set(slot.dayOfWeek, slot);
    });

    const uniqueTimes = Array.from(slotsByTimeAndDay.keys()).sort((a, b) => {
        const aIsEarly = a < DAY_START_TIME;
        const bIsEarly = b < DAY_START_TIME;
        if (aIsEarly && !bIsEarly) return 1;
        if (!aIsEarly && bIsEarly) return -1;
        return a.localeCompare(b);
    });

    if (slots.length === 0) {
        return (
            <Typography variant="body1" color="textSecondary" align="center" sx={{ mt: 5 }}>
                У вас ще немає жодного слоту. Натисніть "+" над днем тижня, щоб додати перший.
            </Typography>
        )
    }

    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', gap: '8px' }}>
            {/* Header */}
            <Box />
            {daysOfWeek.map(day => (
                <Box key={day.id} sx={{ textAlign: 'center' }}>
                    <Typography variant="h6">{day.name}</Typography>
                    <IconButton size="small" onClick={() => handleOpenCreateModal(day.id)}>
                        <AddCircleOutlineIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}

            {/* Rows */}
            {uniqueTimes.map(time => (
                <React.Fragment key={time}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pr: 1}}>
                        <Typography variant="body2" color="textSecondary">{time.substring(0, 5)}</Typography>
                    </Box>
                    {daysOfWeek.map(day => {
                        const slot = slotsByTimeAndDay.get(time)?.get(day.id);
                        return (
                            <Box key={`${time}-${day.id}`} sx={{ minHeight: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {slot ? (
                                    <Paper 
                                        elevation={3}
                                        sx={{
                                            p: 1,
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            backgroundColor: slot.slotType.color,
                                            color: 'white',
                                            cursor: 'pointer',
                                            '&:hover': { opacity: 0.9, boxShadow: '0 4px 8px rgba(0,0,0,0.2)' }
                                        }}
                                        onClick={() => handleOpenEditModal(slot)}
                                    >
                                        <Typography variant="caption" fontWeight="bold" align="center">
                                            {slot.slotName || slot.slotType.name}
                                        </Typography>
                                    </Paper>
                                ) : (
                                    <Box sx={{width: '100%', height: '100%'}} /> // Placeholder
                                )}
                            </Box>
                        );
                    })}
                </React.Fragment>
            ))}
        </Box>
    );
  }

  const renderContent = () => {
    if (loading) {
      return <CircularProgress />;
    }
    if (error) {
        return <Alert severity="error" onClose={() => setError(null)} sx={{ width: '100%', mb: 2 }}>{error}</Alert>
    }
    return renderTimelineContent();
  };

  return (
    <Box>
        <Typography variant="h5" sx={{ mb: 3 }}>Слоти Розпорядку Дня</Typography>
        {renderContent()}
        {isModalOpen && (
          <ScheduleSlotEditModal 
              open={isModalOpen}
              onClose={handleCloseModal}
              onSave={handleSave}
              onDelete={handleDeleteRequest}
              slotData={editingSlot}
              slotTypes={slotTypes}
          />
      )}
      {renderConfirmDeleteDialog()}
    </Box>
  );
};
